import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Lock, CheckCircle2 } from "lucide-react";
import { PERFORMANCE_LABELS, OFFERING_LINKS } from "../config/site";

const ILLUSTRATIVE = [
  { m: "M1", v: 100 }, { m: "M2", v: 104 }, { m: "M3", v: 99 }, { m: "M4", v: 108 },
  { m: "M5", v: 114 }, { m: "M6", v: 110 }, { m: "M7", v: 120 }, { m: "M8", v: 117 },
  { m: "M9", v: 128 }, { m: "M10", v: 124 }, { m: "M11", v: 134 }, { m: "M12", v: 141 },
];

const FIELDS = [
  "Historical returns",
  "Maximum drawdown",
  "Reporting period",
  "Realised performance",
  "Relevant risk metrics",
  "Fees and expenses",
  "Data source",
  "Last-updated date",
];

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs font-mono text-gold-200 border border-gold-400/30">
      Index {payload[0].value}
    </div>
  );
}

export default function PerformancePanel() {
  return (
    <div data-testid="performance-panel" className="grid gap-6 lg:grid-cols-[1.25fr_1fr]">
      {/* Illustrative chart */}
      <div className="glass rounded-3xl p-6 sm:p-8 flex flex-col">
        <div
          data-testid="performance-illustrative-label"
          className="mb-6 rounded-lg border border-gold-400/35 bg-gold-400/[0.08] px-4 py-2.5"
        >
          <p className="font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-gold-200 text-center">
            Illustrative example — not actual performance
          </p>
        </div>
        <div className="h-[260px] sm:h-[300px]" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={ILLUSTRATIVE} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFC107" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#FFC107" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="m" tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[90, 150]} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(255,193,7,0.3)" }} />
              <Area
                type="monotone"
                dataKey="v"
                stroke="#FFC107"
                strokeWidth={2}
                fill="url(#goldFill)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
          Schematic curve shown solely to demonstrate how verified performance will be presented
          once available. It represents no product, period or return.
        </p>
      </div>

      {/* Verified data contract + placeholders */}
      <div className="flex flex-col gap-5">
        <div className="glass-gold rounded-3xl p-6 sm:p-7">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-400/80 mb-4">
            What verified data will report
          </p>
          <ul className="flex flex-col gap-2.5">
            {FIELDS.map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-[13px] text-zinc-300">
                <CheckCircle2 size={14} className="text-gold-400 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          {OFFERING_LINKS.map((o) => (
            <div
              key={o.to}
              data-testid="performance-placeholder"
              className="flex items-center gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-5 py-4"
            >
              <Lock size={14} className="text-zinc-500 shrink-0" />
              <div>
                <p className="text-[13px] font-medium text-zinc-200">{o.label}</p>
                <p className="text-xs text-zinc-500">
                  Verified performance data will be published here when available.
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function LabelLegend() {
  return (
    <div
      data-testid="performance-label-legend"
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mt-10"
    >
      {PERFORMANCE_LABELS.map((l) => (
        <div key={l.tag} className="glass rounded-2xl p-5">
          <span className="inline-flex items-center gap-2 mb-3">
            <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-50">
              {l.tag}
            </span>
          </span>
          <p className="text-xs text-zinc-500 leading-relaxed">{l.text}</p>
        </div>
      ))}
    </div>
  );
}